(() => {
  'use strict';

  const ROOT_SELECTOR = [
    '.ks-hero',
    '.ks-solution-paths',
    '.ks-trust',
    '.ks-process',
    '.ks-social-proof',
    '.ks-project-gallery',
    '.ks-offers',
    '.ks-faq-section',
    '.ks-locations',
    '.ks-contact',
    '.ks-cta'
  ].join(',');

  const MOTION_MAP = [
    {
      root: '.ks-hero',
      groups: [
        ['.ks-hero__eyebrow', '.ks-hero__heading', '.ks-hero__subheading', '.ks-hero__actions', '.ks-hero__service-note'],
        ['.ks-hero__media'],
        ['.ks-hero__trust-item']
      ],
      immediate: true
    },
    {
      root: '.ks-solution-paths',
      groups: [['.ks-section-header'], ['.ks-solution-card'], ['.ks-solution-paths__footer']]
    },
    {
      root: '.ks-trust',
      groups: [['.ks-trust__header'], ['.ks-trust-card'], ['.ks-assurance-panel']]
    },
    {
      root: '.ks-process',
      groups: [['.ks-process__header'], ['.ks-process__step'], ['.ks-process__footer']]
    },
    {
      root: '.ks-social-proof',
      groups: [
        ['.ks-social-proof__header', '.ks-social-proof__evidence'],
        ['.ks-social-proof-card'],
        ['.ks-social-proof__controls', '.ks-social-proof__footer']
      ]
    },
    {
      root: '.ks-project-gallery',
      groups: [
        ['.ks-project-gallery__header'],
        ['.ks-project-card'],
        ['.ks-project-gallery__navigation', '.ks-project-gallery__footer']
      ]
    },
    {
      root: '.ks-offers',
      groups: [
        ['.ks-offers__header', '.ks-offers__context'],
        ['.ks-offers__card'],
        ['.ks-offers__recommendation', '.ks-offers__assurances-wrap', '.ks-offers__cta']
      ]
    },
    {
      root: '.ks-faq-section',
      groups: [['.ks-faq-section__header'], ['.ks-faq-section__item'], ['.ks-faq-section__footer']]
    },
    {
      root: '.ks-locations',
      groups: [
        ['.ks-locations__header', '.ks-locations__coverage'],
        ['.ks-location-card'],
        ['.ks-locations__confirmation']
      ]
    },
    {
      root: '.ks-contact',
      groups: [
        ['.ks-contact__header', '.ks-contact__reassurance'],
        ['.ks-contact__form-panel'],
        ['.ks-contact__expectations', '.ks-contact__direct']
      ]
    },
    {
      root: '.ks-cta',
      groups: [
        ['.ks-cta__eyebrow', '.ks-cta__heading', '.ks-cta__text'],
        ['.ks-cta__actions'],
        ['.ks-cta__reassurance-item']
      ]
    }
  ];

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const prepared = new WeakSet();
  let observer = null;
  let safetyTimer = null;

  const reveal = (element) => {
    if (!(element instanceof Element)) return;
    element.classList.add('ks-motion--visible');
    observer?.unobserve(element);
  };

  const revealWithin = (root = document) => {
    if (root.matches?.('.ks-motion')) reveal(root);
    root.querySelectorAll?.('.ks-motion').forEach(reveal);
  };

  const createObserver = () => {
    if (observer || reducedMotion.matches || !('IntersectionObserver' in window)) return observer;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) reveal(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08
      }
    );

    return observer;
  };

  const prepareElement = (element, delayIndex, immediate) => {
    if (!(element instanceof HTMLElement) || prepared.has(element)) return;

    prepared.add(element);
    element.classList.add('ks-motion');
    element.style.setProperty('--ks-motion-delay', `${Math.min(delayIndex, 6) * 70}ms`);

    if (reducedMotion.matches || immediate || !('IntersectionObserver' in window)) {
      requestAnimationFrame(() => reveal(element));
      return;
    }

    createObserver()?.observe(element);
  };

  const initializeSection = (section) => {
    const config = MOTION_MAP.find(({ root }) => section.matches(root));
    if (!config) return;

    let sequence = 0;

    config.groups.forEach((selectors) => {
      const elements = Array.from(section.querySelectorAll(selectors.join(',')));

      elements.forEach((element, itemIndex) => {
        prepareElement(element, sequence + itemIndex, config.immediate);
      });

      if (elements.length) sequence += Math.min(elements.length, 2);
    });
  };

  const initializeWithin = (root = document) => {
    try {
      if (root.matches?.(ROOT_SELECTOR)) initializeSection(root);
      root.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeSection);
    } catch (error) {
      revealWithin(root);
      if (window.Shopify?.designMode) console.warn('[KS animations] Reveal fallback applied.', error);
    }
  };

  const showEverything = () => {
    revealWithin(document);
    observer?.disconnect();
    observer = null;
  };

  const start = () => {
    initializeWithin();

    // Fail open: content must never remain hidden if a browser extension,
    // third-party script or interrupted lifecycle prevents observer callbacks.
    safetyTimer = window.setTimeout(() => {
      document.querySelectorAll('.ks-motion:not(.ks-motion--visible)').forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) reveal(element);
      });
    }, 2200);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('.ks-motion').forEach((element) => observer?.unobserve(element));
  });

  reducedMotion.addEventListener?.('change', (event) => {
    if (event.matches) showEverything();
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) revealWithin(document);
  });

  window.addEventListener(
    'pagehide',
    () => {
      if (safetyTimer) window.clearTimeout(safetyTimer);
      observer?.disconnect();
    },
    { once: true }
  );
})();
